
import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CalendarIcon, Clock } from 'lucide-react';
import { format } from 'date-fns';

interface EventDateTimeFieldsProps {
  eventDate: Date | undefined;
  startTime: string;
  endTime: string;
  timezone: string;
  onDateChange: (date: Date | undefined) => void;
  onStartTimeChange: (value: string) => void;
  onEndTimeChange: (value: string) => void;
  onTimezoneChange: (value: string) => void;
}

export const EventDateTimeFields: React.FC<EventDateTimeFieldsProps> = ({
  eventDate,
  startTime,
  endTime,
  timezone,
  onDateChange,
  onStartTimeChange,
  onEndTimeChange,
  onTimezoneChange,
}) => {
  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const displayTime = new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        });
        times.push({ value: timeString, display: displayTime });
      }
    }
    return times;
  };

  const timeOptions = generateTimeOptions();

  const formatTimeForDisplay = (time: string) => {
    if (!time) return '';
    try {
      return new Date(`2000-01-01T${time}`).toLocaleTimeString([], { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      }) + ' CST';
    } catch {
      return time;
    }
  };

  return (
    <>
      <div>
        <Label>Date</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-start text-left font-normal bg-gray-900 border-gray-700 text-white hover:bg-gray-800"
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {eventDate ? format(eventDate, 'PPP') : <span>Pick a date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-gray-900 border-gray-700">
            <Calendar
              mode="single"
              selected={eventDate}
              onSelect={onDateChange}
              initialFocus
              className="bg-gray-900 text-white"
            />
          </PopoverContent>
        </Popover>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label htmlFor="start_time">Start Time</Label>
          <Select value={startTime} onValueChange={onStartTimeChange}>
            <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
              <Clock className="mr-2 h-4 w-4" />
              <SelectValue placeholder="Start time">
                {startTime ? formatTimeForDisplay(startTime) : 'Select time'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-700 text-white max-h-60">
              {timeOptions.map((time) => (
                <SelectItem 
                  key={time.value} 
                  value={time.value} 
                  className="text-white hover:bg-gray-800"
                >
                  {time.display}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label htmlFor="end_time">End Time</Label>
          <Select value={endTime} onValueChange={onEndTimeChange}>
            <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
              <Clock className="mr-2 h-4 w-4" />
              <SelectValue placeholder="End time">
                {endTime ? formatTimeForDisplay(endTime) : 'Select time'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-700 text-white max-h-60">
              {timeOptions.map((time) => (
                <SelectItem 
                  key={time.value} 
                  value={time.value} 
                  className="text-white hover:bg-gray-800"
                >
                  {time.display}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label htmlFor="timezone">Time Zone</Label>
          <Select value={timezone} onValueChange={onTimezoneChange}>
            <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-700 text-white">
              <SelectItem value="EST" className="text-white hover:bg-gray-800">EST</SelectItem>
              <SelectItem value="CST" className="text-white hover:bg-gray-800">CST</SelectItem>
              <SelectItem value="MST" className="text-white hover:bg-gray-800">MST</SelectItem>
              <SelectItem value="PST" className="text-white hover:bg-gray-800">PST</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </>
  );
};
